import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { baseurl } from "../../../Baseurl";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../api/axiosInstance";
import SignatureCanvas from "react-signature-canvas";

export default function AddDoctor() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [stampFile, setStampFile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const sigCanvasRef = useRef(null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fullName: "",
    shortName: "",
    prefix: "",
    dateOfBirth: "",
    licenseId: "",
    civilId: "",
    passport: "",
    gender: "",
    specialty: "",
    phoneNumber: "",
  });

  // Validation functions
  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^[0-9]{8,15}$/;
    return phoneRegex.test(phone) || phone === "";
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) || email === "";
  };

  const validateLicenseId = (license) => {
    const licenseRegex = /^[A-Z0-9]{3,20}$/i;
    return licenseRegex.test(license) || license === "";
  };

  const validateCivilId = (civilId) => {
    const civilIdRegex = /^[0-9]{9,12}$/;
    return civilIdRegex.test(civilId) || civilId === "";
  };

  const validatePassport = (passport) => {
    const passportRegex = /^[A-Z0-9]{6,12}$/i;
    return passportRegex.test(passport) || passport === "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    let error = "";
    switch (name) {
      case "phoneNumber":
        if (!validatePhoneNumber(value) && value !== "") {
          error = "Phone number must be 8-15 digits";
        }
        break;
      case "email":
        if (!validateEmail(value) && value !== "") {
          error = "Enter a valid email address";
        }
        break;
      case "password":
        if (value && value.length < 6) {
          error = "Password must be at least 6 characters";
        }
        break;
      case "licenseId":
        if (!validateLicenseId(value) && value !== "") {
          error = "License ID must be 3-20 alphanumeric characters";
        }
        break;
      case "civilId":
        if (!validateCivilId(value) && value !== "") {
          error = "Civil ID must be 9-12 digits";
        }
        break;
      case "passport":
        if (!validatePassport(value) && value !== "") {
          error = "Passport must be 6-12 alphanumeric characters";
        }
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleFile = (e) => setFile(e.target.files[0] || null);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.prefix) newErrors.prefix = "Prefix is required";
    if (!formData.fullName) newErrors.fullName = "First name is required";
    if (!formData.shortName) newErrors.shortName = "Last name is required";
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    if (!formData.dateOfBirth) newErrors.dateOfBirth = "Date of birth is required";
    if (!formData.gender) newErrors.gender = "Gender is required";
    if (!formData.specialty) newErrors.specialty = "Speciality is required";
    if (!formData.phoneNumber) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!validatePhoneNumber(formData.phoneNumber)) {
      newErrors.phoneNumber = "Phone number must be 8-15 digits";
    }

    if (formData.phoneNumber && !validatePhoneNumber(formData.phoneNumber)) {
      newErrors.phoneNumber = "Phone number must be 8-15 digits";
    }

    if (formData.licenseId && !validateLicenseId(formData.licenseId)) {
      newErrors.licenseId = "License ID must be 3-20 alphanumeric characters";
    }

    if (formData.civilId && !validateCivilId(formData.civilId)) {
      newErrors.civilId = "Civil ID must be 9-12 digits";
    }

    if (formData.passport && !validatePassport(formData.passport)) {
      newErrors.passport = "Passport must be 6-12 alphanumeric characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const res = await axiosInstance.get(`getAllServiceCategories`);
        if (res.data && res.data.success && Array.isArray(res.data.data)) {
          setCategories(res.data.data);
        }
      } catch (err) {
        // noop; keep dropdown empty on failure
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      Swal.fire("Validation Error", "Please correct the errors in the form", "error");
      return;
    }

    try {
      const data = new FormData();
      Object.entries(formData).forEach(([k, v]) => data.append(k, v));
      data.append("role", "doctor");
      if (file) data.append("personalPhoto", file);
      if (stampFile) data.append("stamp", stampFile);
      if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
        data.append("signature", sigCanvasRef.current.toDataURL("image/png"));
      }

      const res = await axiosInstance.post(`addDoctorDetails`, data, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data.success) {
        Swal.fire("Success", "Doctor added successfully", "success");
        navigate("/Admin/ManageStaff");
      } else {
        Swal.fire("Error", res.data.message || "Failed to add doctor", "error");
      }
    } catch (err) {
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        "Something went wrong";

      Swal.fire("Error", backendMessage, "error");
    }
  };

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div className="col-12">
          <div className="card table-card">
            <div className="tableHeader">
              <h5 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ cursor: 'pointer', color: '#3f75c7', fontWeight: 'bold', fontSize: '1.5rem' }} onClick={() => navigate(-1)} title="Back">&larr;</span>
                Add Doctor
              </h5>
            </div>
            <form onSubmit={handleSubmit} autoComplete="off" className="row g-3 px-3 py-2 mb-3">
              <div className="col-md-6">
                <label className="form-label">Prefix</label>
                <select
                  name="prefix"
                  className={`form-control ${errors.prefix ? "is-invalid" : ""}`}
                  value={formData.prefix}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  <option value="Dr.">Dr.</option>
                  <option value="Mr.">Mr.</option>
                  <option value="Mrs.">Mrs.</option>
                  <option value="Miss">Miss</option>
                </select>
                {errors.prefix && <div className="text-danger small">{errors.prefix}</div>}
              </div>
              {[
                ["fullName", "text", "First Name"],
                ["shortName", "text", "Last Name"],
                ["email", "email", "Email"],
                ["dateOfBirth", "date", "Date of Birth"],
              ].map(([name, type, label]) => (
                <div className="col-md-6" key={name}>
                  <label className="form-label">{label}</label>
                  <input
                    type={type}
                    name={name}
                    className={`form-control ${errors[name] ? "is-invalid" : ""}`}
                    value={formData[name]}
                    onChange={handleChange}
                    autoComplete={name === "password" ? "new-password" : "off"}
                  />
                  {errors[name] && <div className="text-danger small">{errors[name]}</div>}
                </div>
              ))}

              <div className="col-md-6">
                <label className="form-label">License ID</label>
                <input
                  type="text"
                  name="licenseId"
                  className={`form-control ${errors.licenseId ? 'is-invalid' : ''}`}
                  value={formData.licenseId}
                  onChange={handleChange}
                  placeholder="Enter License ID"
                />
                {errors.licenseId && <div className="text-danger small">{errors.licenseId}</div>}
              </div>

              <div className="col-md-6">
                <label className="form-label">Civil ID</label>
                <input
                  type="text"
                  name="civilId"
                  className={`form-control ${errors.civilId ? 'is-invalid' : ''}`}
                  value={formData.civilId}
                  onChange={handleChange}
                  placeholder="Enter Civil ID"
                />
                {errors.civilId && <div className="text-danger small">{errors.civilId}</div>}
              </div>

              <div className="col-md-6">
                <label className="form-label">Passport</label>
                <input
                  type="text"
                  name="passport"
                  className={`form-control ${errors.passport ? 'is-invalid' : ''}`}
                  value={formData.passport}
                  onChange={handleChange}
                  placeholder="Enter Passport Number"
                />
                {errors.passport && <div className="text-danger small">{errors.passport}</div>}
              </div>

              <div className="col-md-6">
                <label className="form-label">Phone Number</label>
                <input
                  type="text"
                  name="phoneNumber"
                  className={`form-control ${errors.phoneNumber ? 'is-invalid' : ''}`}
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="Enter Phone Number"
                />
                {errors.phoneNumber && <div className="text-danger small">{errors.phoneNumber}</div>}
              </div>

              <div className="col-md-6">
                <label className="form-label">Password</label>
                <div className="position-relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className={`form-control ${errors.password ? "is-invalid" : ""}`}
                    value={formData.password}
                    onChange={handleChange}
                    autoComplete="new-password"
                  />
                  <i
                    className={`ti ${showPassword ? "ti-eye-off" : "ti-eye"}`}
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      fontSize: "18px",
                    }}
                  ></i>
                </div>
                {errors.password && <div className="text-danger small">{errors.password}</div>}
              </div>




              <div className="col-md-6">
                <label className="form-label">Gender</label>
                <select
                  name="gender"
                  className={`form-control ${errors.gender ? "is-invalid" : ""}`}
                  value={formData.gender}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                {errors.gender && <div className="text-danger small">{errors.gender}</div>}
              </div>

              <div className="col-md-6">
                <label className="form-label">Speciality</label>
                <select
                  name="specialty"
                  className={`form-control ${errors.specialty ? "is-invalid" : ""}`}
                  value={formData.specialty}
                  onChange={handleChange}
                >
                  <option value="">{loadingCategories ? "Loading..." : "Select Speciality"}</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.category_name}>{c.category_name}</option>
                  ))}
                </select>
                {errors.specialty && <div className="text-danger small">{errors.specialty}</div>}
              </div>
              <div className="col-md-6">
                <label className="form-label">Photo</label>
                <input type="file" className="form-control" onChange={handleFile} />
              </div>

              {/* Stamp Upload */}
              <div className="col-md-6">
                <label className="form-label">
                  Stamp <span className="text-muted" style={{ fontSize: "11px" }}>(optional – PNG)</span>
                </label>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  className="form-control"
                  onChange={(e) => setStampFile(e.target.files[0] || null)}
                />
                {stampFile && (
                  <img
                    src={URL.createObjectURL(stampFile)}
                    alt="Stamp preview"
                    style={{ marginTop: "8px", maxHeight: "80px", border: "1px solid #dee2e6", borderRadius: "6px" }}
                  />
                )}
              </div>

              {/* Signature Pad */}
              <div className="col-12">
                <label className="form-label">
                  Signature <span className="text-muted" style={{ fontSize: "11px" }}>(optional – draw below)</span>
                </label>
                <div style={{ border: "1px solid #dee2e6", borderRadius: "6px", display: "inline-block", background: "#fff" }}>
                  <SignatureCanvas
                    ref={sigCanvasRef}
                    penColor="#000"
                    canvasProps={{ width: 400, height: 120, style: { display: "block" } }}
                  />
                </div>
                <div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary mt-1"
                    onClick={() => sigCanvasRef.current?.clear()}
                  >
                    Clear Signature
                  </button>
                </div>
              </div>

              <div className="col-12 text-center">
                <button type="submit" className="bgBtn">Add Doctor</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
